import { Section } from '@all-hands-hq/plex-graphql-schema';
import {
  Button,
  Card,
  CircularProgress,
  createStyles,
  DialogContent,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
} from '@material-ui/core';
import React, { useReducer } from 'react';

import {
  FullBoardTemplateFragment,
  useCreateBoardFromTemplateMutation,
  useGetBoardTemplatesQuery,
  useGetPlexDetailsQuery,
} from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { notEmpty } from '../../../../utils/notEmpty';
import { PlexName } from '../../common/PlexName';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { GoogleOutlinePreview } from '../import_gdocs/GoogleDocPreview';
import { SelectedSectionsAction, selectedSectionsReducer } from '../import_gdocs/selectedSectionsReducer';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';
import { useDialogState } from '../useDialogState';

type Props = {
  plexId: string;
};

export const CreateBoardFromTemplateDialog = ({ plexId }: Props): React.ReactElement => {
  const classes = useStyles();
  const [, setDialogState] = useDialogState();
  const { data: plexData, loading: plexLoading } = useGetPlexDetailsQuery({ variables: { plexId } });
  const { data: boardTemplatesData } = useGetBoardTemplatesQuery();
  // TODO: error checking
  const [createBoard, { loading: createLoading }] = useCreateBoardFromTemplateMutation();
  const [state, dispatch] = useReducer(templateSelectionReducer, null);

  const handleCreate = (): void => {
    if (state != null) {
      createBoard({
        variables: {
          plexId,
          templateId: state.templateId,
          questionTemplateIds: [...state.selectedSections].map((section) => section.title.headingId).filter(notEmpty),
        },
        update: (cache, { data }) => {
          if (data?.createBoardFromTemplate != null) {
            cache.evict({
              id: cache.identify({ __typename: 'Plex', id: data.createBoardFromTemplate.plexId }),
              fieldName: 'boards',
            });
          }
        },
      }).then(({ data }) => {
        setDialogState(null, 'replace');
        logAnalytics({ name: 'created board', details: { plexId: data?.createBoardFromTemplate.id } });
      });
    }
  };

  if (plexData?.loggedIn?.plexById == null || boardTemplatesData == null) {
    return <CircularProgress size={24} />;
  }

  return (
    <div className={classes.root}>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <DialogContent>
        <FormControl variant="outlined" size="medium" fullWidth>
          <InputLabel id="template-label" classes={{ root: classes.templateLabel }}>
            Template
          </InputLabel>
          <Select
            labelId="template-label"
            value={state?.templateId || ''}
            onChange={(event): void => {
              const templateId = event.target.value as string;
              const template = boardTemplatesData?.boardTemplates.find(
                (boardTemplate) => boardTemplate.id === templateId
              );
              if (template != null) {
                const document = templateAsDocument(template);

                dispatch({ type: 'SET_TEMPLATE', templateId, document });
              }
            }}
          >
            {boardTemplatesData.boardTemplates.map((boardTemplate) => (
              <MenuItem key={boardTemplate.id} value={boardTemplate.id}>
                {boardTemplate.name} - {boardTemplate.templates.length} Facts
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {state != null && (
          <Card className={classes.documentPreview}>
            <GoogleOutlinePreview
              document={state.document}
              selectedSections={[...state.selectedSections]}
              faqBoardSections={new Set([state.document])}
              faqSections={new Set(state.document.children)}
              onSelectionRowClick={(section: Section): void => dispatch({ type: 'ROW_CLICK', section })}
              onSelectionCheckboxClick={(section: Section): void => dispatch({ type: 'CHECKBOX_CLICK', section })}
              onUpdateSelection={(selectedSections: Section[]): void => {
                dispatch({ type: 'UPDATE_SELECTION', selectedSections });
              }}
            />
          </Card>
        )}
      </DialogContent>

      <DialogActions
        cta={
          <Button
            variant="contained"
            color="primary"
            disabled={state == null || createLoading || plexLoading}
            onClick={handleCreate}
          >
            Create board
          </Button>
        }
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
    templateLabel: {
      backgroundColor: '#ffffff',
    },
    documentPreview: {
      marginTop: spacings.normal,
      padding: spacings.normal,
      textAlign: 'center',
    },
  })
);

type TemplateSelectionState = {
  templateId: string;
  document: Section;
  selectedSections: Set<Section>;
} | null;

type TemplateSelectionAction =
  | {
      type: 'SET_TEMPLATE';
      templateId: string;
      document: Section;
    }
  | SelectedSectionsAction;

const templateSelectionReducer = (
  state: TemplateSelectionState,
  action: TemplateSelectionAction
): TemplateSelectionState => {
  switch (action.type) {
    case 'SET_TEMPLATE': {
      const { templateId, document } = action;

      return {
        templateId,
        document,
        selectedSections: new Set(Section.getDescendentsInclusive(action.document)),
      };
    }

    case 'ROW_CLICK':
    case 'CHECKBOX_CLICK':
    case 'UPDATE_SELECTION': {
      if (state != null) {
        return {
          ...state,
          selectedSections: selectedSectionsReducer(state.selectedSections, action),
        };
      } else {
        return state;
      }
    }
  }
};

const templateAsDocument = (boardTemplate: FullBoardTemplateFragment): Section => {
  const root: Section = {
    title: { type: 'paragraph', children: [{ text: boardTemplate.name }] },
    level: 0,
    startIndex: -1,
    endIndex: boardTemplate.templates.length,
    parent: null,
    children: [],
  };

  boardTemplate.templates.forEach((questionTemplate, idx) => {
    const section: Section = {
      title: { type: 'paragraph', children: [{ text: questionTemplate.text }], headingId: questionTemplate.id },
      level: 0,
      startIndex: idx,
      endIndex: idx + 1,
      parent: root,
      children: [],
    };

    root.children.push(section);
  });

  return root;
};
